import { Stack, Typography, Paper, CircularProgress, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';

import { useGenerateNegotiationAI } from '@/api/endpoints/community/useGenerateNegotiationAI';

interface NegotiationAITabProps {
  communityId: number;
  negotiationId: number;
}

// Define markdown components outside render to avoid ESLint warnings
const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mb: 1 }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Typography component="ul" sx={{ mb: 1, pl: 2 }}>
      {children}
    </Typography>
  ),
  ol: ({ children }) => (
    <Typography component="ol" sx={{ mb: 1, pl: 2 }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography component="strong" sx={{ fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
};

const NegotiationAITab: React.FC<NegotiationAITabProps> = ({ communityId, negotiationId }) => {
  const [aiResponse, setAiResponse] = useState<string>('');
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  const { mutate: generateAI, isPending } = useGenerateNegotiationAI();

  // Auto-trigger AI request on component mount
  useEffect(() => {
    if (!hasAutoLoaded && negotiationId && communityId) {
      setHasAutoLoaded(true);
      generateAI(
        {
          community_id: Number(communityId),
          negotiation_id: Number(negotiationId),
        },
        {
          onSuccess: (data: { response: string }) => {
            setAiResponse(data.response);
          },
          onError: () => {
            enqueueSnackbar('Failed to generate AI response', { variant: 'error' });
          },
        }
      );
    }
  }, [hasAutoLoaded, negotiationId, communityId, generateAI]);

  return (
    <Stack spacing={3}>
      <Typography variant="h6">AI Negotiation Assistant</Typography>

      {/* Show loading state if auto-loading */}
      {isPending && !aiResponse && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {/* Show AI response if available */}
      {aiResponse && (
        <Paper elevation={2} sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            AI Response:
          </Typography>
          <ReactMarkdown components={markdownComponents}>{aiResponse}</ReactMarkdown>
        </Paper>
      )}
    </Stack>
  );
};

export default NegotiationAITab;
